<template>
  <div class="deposit-source">
    <div class="deposit-source-title">1. {{ $t('deposit.source') }}</div>
    <div class="deposit-source-content">
      <div class="balances">
        <label
          class="balances-item"
          :class="{
            'balances-item--active': selectedPayType === 'balance',
          }"
        >
          <input
            type="radio"
            :disabled="isForceBonusEnabled"
            :checked="selectedPayType === 'balance'"
            @change="$emit('on-change', 'balance')"
          />
          <img :src="getPic(locale)" class="balances-icon" alt="main balance" />
          <span class="balances-info">
            <span class="balances-title">
              {{ $t('deposit.ballance') }}
              {{ $t('currency.currency_name') }}
            </span>
            <span class="balances-value">
              {{
                activeAccount.balance
                  ? locale === 'USA'
                    ? numberWithSpacesFix2(activeAccount.balance)
                    : numberWithSpacesCeil(activeAccount.balance)
                  : 0
              }}
              {{ $t('currency.currency_symbol') }}
            </span>
          </span>
        </label>
        <label
          v-if="activeAccount.cashback_balance && activeAccount.cashback_balance > 0"
          class="balances-item"
          :class="{
            'balances-item--active': selectedPayType === 'cashback',
          }"
        >
          <input
            type="radio"
            :disabled="isForceBonusEnabled"
            :checked="selectedPayType === 'cashback'"
            @change="$emit('on-change', 'cashback')"
          />
          <img :src="getPic(locale)" class="balances-icon" alt="main balance" />
          <span class="balances-info">
            <span class="balances-title">
              {{ $t('deposit.cashback') }}
              {{ $t('currency.currency_name') }}
            </span>
            <span class="balances-value">
              {{
                activeAccount.cashback_balance
                  ? locale === 'USA'
                    ? numberWithSpacesFix2(activeAccount.cashback_balance)
                    : numberWithSpacesCeil(activeAccount.cashback_balance)
                  : 0
              }}
              {{ $t('currency.currency_symbol') }}
            </span>
          </span>
        </label>
      </div>
    </div>

    <div v-if="payWithBonusesString" class="deposit-source-message">
      <div class="deposit-source-title--small" v-html="payWithBonusesString" />
    </div>
  </div>
</template>

<script>
import numberWithSpacesFix2 from '@/mixin/numberWithSpaces';
import { useActiveAccount, useCurrencyRates } from '../../mixin';
export default {
  name: 'DepositSource',
  mixins: [numberWithSpacesFix2, useActiveAccount, useCurrencyRates],
  props: {
    selectedPayType: {
      type: String,
      required: true,
    },
    selectedAccount: {
      type: Object,
      required: true,
    },
  },
  computed: {
    locale: () => process.env.VUE_APP_LOCALE,
    localCurrency() {
      return this.locale === 'USA' ? 'USD' : 'RUB';
    },
    payWithBonusesString() {
      if (!this.oldCashbackBalance) return '';
      return `У вас накопились бонусы! <strong>${this.numberWithSpacesCeil(
        this.oldCashbackBalance
      )}</strong> бонусов были начислены более 60 дней назад, нужно их использовать.`;
    },
    oldCashbackBalance() {
      return Number(this.activeAccount?.old_cashback_balance ?? 0);
    },
    isForceBonusEnabled() {
      // Если в настройках аккаунта отключен принудительный выбор бонусов
      if (!this.activeAccount.is_force_bonus) return false;

      const oldCashbackBalance = this.oldCashbackBalance;
      if (!oldCashbackBalance || !this.selectedAccount) return false;
      const minDeposit = this.selectedAccount.min_deposit ?? 0;
      if (!oldCashbackBalance) return false;
      const balanceValueInAccountCurrency = this.convertCurrency(
        this.localCurrency,
        this.selectedAccount.service_currency,
        +oldCashbackBalance
      );
      return balanceValueInAccountCurrency >= minDeposit;
    },
  },
  watch: {
    selectedAccount: {
      handler: (v) => {
        if (!v) return;
      },
      deep: true,
    },
    isForceBonusEnabled(v) {
      // Для отключенной настройки не принуждаем к выбору бонусов
      if (!v || !this.activeAccount.is_force_bonus) return;
      this.$emit('on-change', 'cashback');
    },
  },
  mounted() {
    // Если включен принудительный выбор бонусов, выбираем бонусы автоматически
    if (this.isForceBonusEnabled) {
      this.$emit('on-change', 'cashback');
    }
  },
  methods: {
    getPic(locale) {
      if (locale === 'RU') {
        return require('@/assets/icons/icon-wallet.svg');
      } else {
        return require('@/assets/icons/icon-wallet-red.svg');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.deposit-source {
  margin-bottom: 4.4rem;
}
.deposit-source-title {
  font-weight: 700;
  font-size: 2rem;
  line-height: 2.3rem;
  color: #333333;
  margin-bottom: 1.8rem;

  &--small {
    font-size: 1.6rem;
    line-height: 2.1rem;
  }
}
.deposit-source-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  &.add-x-scroll {
    overflow-x: auto;
    min-height: 55rem;
  }
}

.deposit-source-message {
  background: #f1f4fa;
  border-radius: 2px;
  padding: 0.9rem;
  margin-top: 0.9rem;
}

.balances {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  &-item {
    position: relative;
    background: #ffffff;
    border-radius: 10px;
    border: 2px solid #fff;
    box-shadow: 0 3px 10px rgba(25, 47, 79, 0.12);
    width: 24rem;
    height: 9.2rem;
    display: flex;
    align-items: center;
    padding: 0 1.8rem;
    transition: 0.3s ease-out;
    cursor: pointer;

    &.disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &::before {
      content: '';
      display: block;
      width: 1.8rem;
      height: 1.8rem;
      border-radius: 50%;
      border: 1px solid #d1d4d8;
      position: absolute;
      top: 0.7rem;
      right: 0.7rem;
    }

    &--active {
      box-shadow: none;
      border: 2px solid var(--primary);

      &::before {
        content: '';
        display: block;
        width: 1.8rem;
        height: 1.8rem;
        border-radius: 50%;
        background: url('../../assets/icons/icon-done.svg') var(--primary) no-repeat center;
        background-size: 80%;
        position: absolute;
        border: none;
        top: 0.7rem;
        right: 0.7rem;
        opacity: 0;
        opacity: 1;
      }
    }

    &:last-of-type {
      margin-right: 0;
    }

    input {
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
    }
  }

  &-icon {
    width: 3.3rem;
    margin-right: 1.4rem;
  }

  &-info {
    display: flex;
    flex-direction: column;
  }

  &-title {
    font-size: 1.6rem;
    line-height: 1.9rem;
  }

  &-value {
    font-weight: 700;
    font-size: 2rem;
    line-height: 2.3rem;
    margin-top: 0.7rem;
  }
}
</style>
